import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Columns as ColumnsIcon,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  Menu,
  MessageSquare,
  MoreVertical,
  Phone,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Smartphone,
  Sparkles,
  Trash2,
  Video,
  X,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/toast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { CopyButton } from "../../components/ui/copy-button";
import { Badge } from "../../components/ui/badge";
import { ActionMenu } from "../../components/ui/action-menu";
import { Dropdown } from "../../components/ui/dropdown";
import { useNavStore } from "../../components/layout/nav-store";
import { cn } from "../../lib/utils";
import { templatesApi, type TemplateButton, type WhatsAppTemplate } from "./api";
import { useResolvedMediaUrl } from "./use-resolved-media";

const primary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-brand-hover disabled:opacity-50";
const secondary =
  "inline-flex items-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-2 text-xs font-semibold text-ink shadow-2xs transition hover:bg-soft disabled:opacity-50 dark:bg-[#1f1c1a]";

type StatusFilter = "all" | "active" | "pending" | "draft" | "rejected";
type KindFilter = "all" | "normal" | "carousel";
type ViewMode = "showcase" | "split";

// ── WhatsApp Syntax Formatter (Bold, Italic, Strike, Monospace, Variables) ──
function FormattedWhatsAppText({
  text,
  variableValues,
  compact = false,
}: {
  text: string;
  variableValues?: Record<string, string>;
  compact?: boolean;
}) {
  if (!text) {
    return <span className="text-ink-3 italic text-xs">No message body provided.</span>;
  }
  const lines = text.split("\n");
  return (
    <span
      className={cn(
        "whitespace-pre-wrap break-words leading-relaxed text-ink dark:text-ink-2",
        compact ? "text-[11px]" : "text-xs",
      )}
    >
      {lines.map((line, lIdx) => {
        const parts = line.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```|\{\{\d+\}\})/g);
        return (
          <span key={lIdx}>
            {parts.map((part, pIdx) => {
              if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
                return (
                  <strong key={pIdx} className="font-bold text-ink">
                    {part.slice(1, -1)}
                  </strong>
                );
              }
              if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
                return (
                  <em key={pIdx} className="italic text-ink-2">
                    {part.slice(1, -1)}
                  </em>
                );
              }
              if (part.startsWith("~") && part.endsWith("~") && part.length > 2) {
                return (
                  <del key={pIdx} className="line-through opacity-75">
                    {part.slice(1, -1)}
                  </del>
                );
              }
              if (part.startsWith("```") && part.endsWith("```") && part.length > 6) {
                return (
                  <code
                    key={pIdx}
                    className="rounded bg-black/10 px-1 py-0.5 font-mono text-[10px] dark:bg-white/10"
                  >
                    {part.slice(3, -3)}
                  </code>
                );
              }
              if (/^\{\{\d+\}\}$/.test(part)) {
                const varNum = part.slice(2, -2);
                const customVal = variableValues?.[varNum];
                return (
                  <span
                    key={pIdx}
                    className="mx-0.5 inline-block rounded bg-sky-100 px-1.5 py-0.2 font-mono text-[10px] font-semibold text-sky-800 ring-1 ring-sky-300/60 dark:bg-sky-950/60 dark:text-sky-300 dark:ring-sky-800"
                  >
                    {customVal || part}
                  </span>
                );
              }
              return part;
            })}
            {lIdx < lines.length - 1 && "\n"}
          </span>
        );
      })}
    </span>
  );
}

// ── Bulletproof Media Display (Never shows broken browser <img>) ──
export function TemplateMediaDisplay({
  template,
  className = "h-36 w-full",
  cardIndex = 0,
}: {
  template: WhatsAppTemplate;
  className?: string;
  cardIndex?: number;
}) {
  const isVideo = template.type === "VIDEO";
  const isCarousel = template.template_kind === "carousel";
  const mediaUrl = isCarousel
    ? template.cards?.[cardIndex]?.header_media_url || template.header_media_url || ""
    : template.header_media_url || "";

  const resolved = useResolvedMediaUrl(mediaUrl);
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoadError(false);
    setLoaded(false);
  }, [resolved, mediaUrl]);

  // If text-only template with no media attached:
  if (template.type === "TEXT" && !mediaUrl) {
    return null;
  }

  // Still resolving remote media URL: show pulse skeleton
  if (mediaUrl && !resolved && !loadError) {
    return (
      <div className={cn("relative overflow-hidden rounded-lg bg-soft animate-pulse flex items-center justify-center border border-line/40 select-none", className)}>
        <div className="flex flex-col items-center gap-1 text-ink-3/50 p-2">
          <ImageIcon className="h-5 w-5 animate-pulse" />
          <span className="font-mono text-[9px]">Loading asset…</span>
        </div>
      </div>
    );
  }

  // Fallback banner when image is missing or failed to load
  if (!mediaUrl || loadError) {
    return (
      <div
        className={cn(
          "relative flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-soft/90 via-line/30 to-soft border border-line/50 text-ink-3 select-none",
          className,
        )}
      >
        <div className="absolute inset-0 opacity-15 [background-image:radial-gradient(#888_1px,transparent_1px)] [background-size:10px_10px]" />
        <div className="relative z-10 flex flex-col items-center gap-1.5 p-3 text-center">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-paper shadow-2xs border border-line/60 text-brand">
            {isVideo ? (
              <Video className="h-4 w-4" />
            ) : isCarousel ? (
              <Layers className="h-4 w-4" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )}
          </div>
          <span className="font-mono text-[10px] font-semibold text-ink-2">
            {isVideo
              ? "Video Header"
              : isCarousel
                ? `Card ${cardIndex + 1} Visual`
                : "Image Asset"}
          </span>
          <span className="text-[9px] text-ink-3">
            {isCarousel ? "Carousel Slide Preview" : template.category || "WhatsApp Header"}
          </span>
        </div>
        {isCarousel && (
          <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-0.5 font-mono text-[9px] font-semibold text-white backdrop-blur-xs">
            {template.cards?.length ?? 0} cards
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden rounded-lg bg-black/5 dark:bg-black/30", className)}>
      {isVideo ? (
        <div className="relative h-full w-full">
          <video
            src={resolved}
            className="h-full w-full object-cover"
            muted
            preload="metadata"
            onError={() => setLoadError(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-[1px]">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-brand shadow-md transition-transform group-hover:scale-110">
              <Play className="h-4 w-4 fill-current ml-0.5" />
            </div>
          </div>
        </div>
      ) : (
        <div className="relative h-full w-full">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-soft animate-pulse">
              <ImageIcon className="h-5 w-5 text-ink-3/40" />
            </div>
          )}
          <img
            src={resolved}
            alt=""
            aria-hidden="true"
            onLoad={() => setLoaded(true)}
            onError={() => setLoadError(true)}
            className={cn(
              "h-full w-full object-cover transition-all duration-300 group-hover:scale-105",
              loaded ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
      )}
      {isCarousel && (
        <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-2 py-0.5 font-mono text-[10px] font-semibold text-white backdrop-blur-xs shadow-xs">
          {template.cards?.length ?? 0} cards
        </span>
      )}
    </div>
  );
}

// ── Realistic WhatsApp Message Bubble Component ──
export function WhatsAppMessageBubble({
  template,
  variableValues,
  activeCardIndex = 0,
  onButtonClick,
  compact = false,
}: {
  template: WhatsAppTemplate;
  variableValues?: Record<string, string>;
  activeCardIndex?: number;
  onButtonClick?: (b: TemplateButton) => void;
  compact?: boolean;
}) {
  const isCarousel = template.template_kind === "carousel";
  const activeCard = isCarousel ? template.cards?.[activeCardIndex] : null;
  const bodyText = isCarousel ? activeCard?.body || template.body : template.body;
  const buttons = isCarousel ? activeCard?.buttons || template.buttons : template.buttons;

  return (
    <div className="relative max-w-full overflow-hidden rounded-2xl rounded-tl-xs border border-[#e2e8f0]/80 bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)] dark:border-[#2a3942] dark:bg-[#202c33]">
      {/* Media Header */}
      <TemplateMediaDisplay
        template={template}
        cardIndex={activeCardIndex}
        className={compact ? "h-28 w-full mb-2" : "h-36 w-full mb-2.5"}
      />

      {/* Message Text */}
      <div className={compact ? "line-clamp-3" : "line-clamp-5"}>
        <FormattedWhatsAppText
          text={bodyText}
          variableValues={variableValues}
          compact={compact}
        />
      </div>

      {/* WhatsApp Attached Buttons */}
      {(buttons?.length ?? 0) > 0 && (
        <div className="mt-2.5 space-y-1 border-t border-[#e9edef] pt-2 dark:border-[#2a3942]">
          {buttons?.map((b, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onButtonClick?.(b)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#e7f0fe]/60 py-1.5 px-2 text-center text-xs font-semibold text-brand transition-colors hover:bg-[#e7f0fe] active:scale-[0.99] dark:bg-brand/15 dark:text-brand"
            >
              {b.type === "URL" && <ExternalLink className="h-3 w-3 shrink-0" />}
              {b.type === "PHONE_NUMBER" && <Phone className="h-3 w-3 shrink-0" />}
              {b.type === "TEMPLATE" && <Layers className="h-3 w-3 shrink-0" />}
              <span className="truncate max-w-[200px]">{b.text}</span>
            </button>
          ))}
        </div>
      )}

      {/* Timestamp & Double Blue Checkmarks */}
      <div className="mt-1.5 flex items-center justify-end gap-1 text-[9px] text-ink-3">
        <span>10:42 AM</span>
        <span className="font-bold text-[#53bdeb]">✓✓</span>
      </div>
    </div>
  );
}

// ── Interactive Live Smartphone Mockup Component (Split & Modal) ──
function LiveSmartphoneDevice({
  template,
  variableValues,
  onVariableChange,
  onOpenStudio,
}: {
  template: WhatsAppTemplate;
  variableValues: Record<string, string>;
  onVariableChange: (vNum: string, val: string) => void;
  onOpenStudio: () => void;
}) {
  const isCarousel = template.template_kind === "carousel";
  const [activeCardIdx, setActiveCardIdx] = useState(0);
  const [simThread, setSimThread] = useState<{ payload: string; response: string }[]>([]);

  const handleButtonClick = (b: TemplateButton) => {
    const payload =
      b.type === "QUICK_REPLY" ? b.text.trim() : (b.value || "").trim() || b.text.trim();
    const response = /stop/i.test(b.text)
      ? "You have been unsubscribed from promotional messages. Reply START to opt back in."
      : `Received reply: "${payload}". Requested action executed successfully.`;
    setSimThread((prev) => [...prev, { payload, response }]);
  };

  const totalCards = template.cards?.length || 1;
  const vars = template.variables ?? [];

  return (
    <div className="flex flex-col items-center">
      {/* Smartphone Chassis */}
      <div className="relative w-full max-w-[340px] overflow-hidden rounded-[2.5rem] border-[7px] border-neutral-900 bg-paper shadow-2xl ring-1 ring-black/10 dark:border-neutral-800 dark:ring-white/10 sm:max-w-[360px]">
        {/* Dynamic Island / Speaker Notch */}
        <div className="absolute top-2 left-1/2 z-30 flex h-4 w-28 -translate-x-1/2 items-center justify-center rounded-full bg-neutral-900">
          <div className="h-2 w-2 rounded-full bg-neutral-800 mr-2" />
          <div className="h-1.5 w-8 rounded-full bg-neutral-800" />
        </div>

        {/* Smartphone Status Bar */}
        <div className="flex h-7 items-center justify-between bg-[#075e54] px-6 text-[10px] font-semibold text-white/90">
          <span>9:41</span>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px]">5G</span>
            <span className="text-[10px]">100%</span>
          </div>
        </div>

        {/* WhatsApp App Header */}
        <div className="flex items-center justify-between bg-[#075e54] px-3.5 py-2.5 text-white shadow-xs">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-white font-bold text-xs">
              <MessageSquare className="h-4 w-4" />
              <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-400 ring-1 ring-[#075e54]" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-xs font-bold leading-tight flex items-center gap-1">
                {template.name}
                <CheckCircle2 className="h-3 w-3 text-sky-300 shrink-0" />
              </p>
              <p className="text-[10px] text-emerald-200">Official Business Account</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/80">
            <Phone className="h-3.5 w-3.5" />
            <MoreVertical className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* WhatsApp Chat Canvas */}
        <div className="flex h-[420px] flex-col justify-between overflow-y-auto bg-[#efeae2]/85 p-3.5 dark:bg-[#0c1317]">
          <div className="space-y-3">
            {/* Date Tag */}
            <div className="text-center">
              <span className="rounded-md bg-paper/90 px-2.5 py-0.5 text-[9px] font-semibold text-ink-3 shadow-2xs dark:bg-black/60">
                TODAY
              </span>
            </div>

            {/* Carousel Navigation (if carousel format) */}
            {isCarousel && totalCards > 1 && (
              <div className="flex items-center justify-between rounded-lg bg-paper/90 px-2 py-1 shadow-2xs dark:bg-black/50">
                <button
                  type="button"
                  disabled={activeCardIdx === 0}
                  onClick={() => setActiveCardIdx((p) => Math.max(0, p - 1))}
                  className="rounded p-1 text-ink-3 hover:text-ink disabled:opacity-30"
                  aria-label="Previous card"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="font-mono text-[10px] font-semibold text-brand">
                  Card {activeCardIdx + 1} of {totalCards}
                </span>
                <button
                  type="button"
                  disabled={activeCardIdx === totalCards - 1}
                  onClick={() => setActiveCardIdx((p) => Math.min(totalCards - 1, p + 1))}
                  className="rounded p-1 text-ink-3 hover:text-ink disabled:opacity-30"
                  aria-label="Next card"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* The Main Template Message Bubble */}
            <WhatsAppMessageBubble
              template={template}
              variableValues={variableValues}
              activeCardIndex={activeCardIdx}
              onButtonClick={handleButtonClick}
            />

            {/* Simulated Conversational Replies */}
            {simThread.map((item, idx) => (
              <div key={idx} className="space-y-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-tr-xs bg-[#d9fdd3] p-2.5 text-xs text-[#111b21] shadow-2xs dark:bg-[#005c4b] dark:text-white">
                  <p>{item.payload}</p>
                  <span className="mt-0.5 block text-right text-[9px] text-[#53bdeb] font-bold">✓✓ Just now</span>
                </div>
                <div className="mr-auto w-fit max-w-[85%] rounded-2xl rounded-tl-xs border border-line/40 bg-white p-2.5 text-xs text-ink shadow-2xs dark:bg-[#202c33]">
                  {item.response}
                </div>
              </div>
            ))}
          </div>

          {/* Reset Simulated Thread */}
          {simThread.length > 0 && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setSimThread([])}
                className="inline-flex items-center gap-1 rounded-full bg-paper px-2.5 py-1 text-[10px] font-semibold text-brand shadow-xs hover:underline dark:bg-[#202c33]"
              >
                <RotateCcw className="h-3 w-3" /> Reset Replies
              </button>
            </div>
          )}
        </div>

        {/* WhatsApp Chat Bar */}
        <div className="flex items-center gap-2 border-t border-line/40 bg-[#f0f2f5] p-2 dark:bg-[#1f2c34]">
          <div className="flex-1 rounded-full bg-white px-3 py-1.5 text-[11px] text-ink-3 dark:bg-[#2a3942]">
            Type a message...
          </div>
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#00a884] text-white">
            <Send className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>

      {/* Dynamic Variables Sandbox Toolbar */}
      <div className="mt-4 w-full max-w-[340px] rounded-xl border border-line/60 bg-paper-card p-3 shadow-card dark:bg-[#292524] sm:max-w-[360px]">
        <div className="flex items-center justify-between border-b border-line pb-2 mb-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-ink">
            <Sparkles className="h-3.5 w-3.5 text-brand" />
            Live Variable Sandbox
          </span>
          <span className="font-mono text-[10px] text-ink-3">
            {vars.length} token{vars.length === 1 ? "" : "s"}
          </span>
        </div>

        {vars.length === 0 ? (
          <p className="text-[11px] text-ink-3 italic py-1 text-center">
            No dynamic tokens ({"{{1}}"}) in this template.
          </p>
        ) : (
          <div className="space-y-2">
            {vars.map((v) => (
              <div key={v} className="flex items-center gap-2">
                <span className="shrink-0 font-mono text-xs font-bold text-brand">{`{{${v}}}`}:</span>
                <input
                  type="text"
                  value={variableValues[String(v)] || ""}
                  onChange={(e) => onVariableChange(String(v), e.target.value)}
                  placeholder={`Value for {{${v}}}`}
                  className="h-7 w-full rounded-md border border-line bg-paper px-2 text-xs text-ink outline-none focus:border-brand dark:bg-[#1f1c1a]"
                />
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onOpenStudio}
          className={`${primary} mt-3 w-full`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Edit in Template Studio
        </button>
      </div>
    </div>
  );
}

// ── Live Simulator Modal (for Grid and Table view) ──
function TemplateLiveTesterModal({
  template,
  onClose,
  onOpenStudio,
}: {
  template: WhatsAppTemplate;
  onClose: () => void;
  onOpenStudio: () => void;
}) {
  const vars = template.variables ?? [];
  const [testVars, setTestVars] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    vars.forEach((v, idx) => {
      init[String(v)] = idx === 0 ? "John Doe" : idx === 1 ? "25% OFF" : `Val_${v}`;
    });
    return init;
  });

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Live Template Tester"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-line bg-elevated p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line pb-3 mb-4">
          <div>
            <h3 className="font-bold text-sm text-ink flex items-center gap-1.5">
              <Smartphone className="h-4 w-4 text-brand" />
              Live Template Simulator
            </h3>
            <p className="text-[11px] text-ink-3">
              Test dynamic substitutions and interactive reply flows
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink"
            aria-label="Close simulator"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <LiveSmartphoneDevice
          template={template}
          variableValues={testVars}
          onVariableChange={(vNum, val) =>
            setTestVars((prev) => ({ ...prev, [vNum]: val }))
          }
          onOpenStudio={onOpenStudio}
        />
      </div>
    </div>,
    document.body
  );
}

// ── Main Templates Page Component ──
export function TemplatesPage() {
  const { token } = useAuth() as any;
  const toast = useToast();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const openMobileNav = useNavStore((s) => s.openMobileNav);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"name" | "status" | "cards">("name");
  const [viewMode, setViewMode] = useState<ViewMode>("showcase");

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [splitVars, setSplitVars] = useState<Record<string, string>>({});
  const [pendingDelete, setPendingDelete] = useState<WhatsAppTemplate | null>(null);
  const [showChoice, setShowChoice] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<WhatsAppTemplate | null>(null);

  const list = useQuery({
    queryKey: ["whatsapp-templates", token],
    queryFn: templatesApi.list,
    enabled: Boolean(token),
    refetchInterval: 30000,
  });

  const allRows = useMemo(() => list.data ?? [], [list.data]);

  // Filter & sort logic
  const filtered = useMemo(() => {
    let rows = [...allRows];

    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter(
        (t) =>
          t.name.toLowerCase().includes(needle) ||
          t.body.toLowerCase().includes(needle) ||
          (t.category || "").toLowerCase().includes(needle),
      );
    }

    if (statusFilter !== "all") {
      rows = rows.filter((t) => {
        const s = (t.status || "draft").toLowerCase();
        if (statusFilter === "active") return s === "approved" || s === "active";
        if (statusFilter === "pending") return s === "pending";
        if (statusFilter === "draft") return s === "draft" || !t.status;
        if (statusFilter === "rejected") return s === "rejected";
        return true;
      });
    }

    if (kindFilter !== "all") {
      rows = rows.filter((t) => {
        const k = t.template_kind === "carousel" ? "carousel" : "normal";
        return k === kindFilter;
      });
    }

    if (categoryFilter !== "all") {
      rows = rows.filter(
        (t) => (t.category || "").toUpperCase() === categoryFilter.toUpperCase(),
      );
    }

    rows.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "status") {
        const sA = (a.status || "").toLowerCase();
        const sB = (b.status || "").toLowerCase();
        return sA.localeCompare(sB);
      }
      if (sortBy === "cards") {
        const cA = a.cards?.length ?? 0;
        const cB = b.cards?.length ?? 0;
        return cB - cA;
      }
      return 0;
    });

    return rows;
  }, [allRows, search, statusFilter, kindFilter, categoryFilter, sortBy]);

  // Selected template for Split view
  const activeSplitTemplate = useMemo(() => {
    if (!filtered.length) return null;
    if (selectedTemplateId) {
      const match = filtered.find((t) => t.template_id === selectedTemplateId);
      if (match) return match;
    }
    return filtered[0];
  }, [filtered, selectedTemplateId]);

  // Aggregate metrics
  const activeCount = allRows.filter((t) =>
    ["approved", "active"].includes((t.status || "").toLowerCase()),
  ).length;
  const pendingCount = allRows.filter(
    (t) => (t.status || "").toLowerCase() === "pending",
  ).length;
  const draftCount = allRows.filter((t) =>
    ["draft", ""].includes((t.status || "").toLowerCase()),
  ).length;
  const carouselCount = allRows.filter((t) => t.template_kind === "carousel").length;

  const del = useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: () => {
      toast.success("Template deleted");
      cache.invalidateQueries({ queryKey: ["whatsapp-templates", token] });
      setPendingDelete(null);
    },
    onError: (err: any) => toast.danger("Could not delete template", err.message),
  });

  return (
    <div className="space-y-4 px-4 pb-12 pt-3 sm:px-5 sm:pt-4 md:px-6">
      {/* ── Top Header Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={openMobileNav}
            aria-label="Open navigation menu"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-sm font-semibold text-ink">Template Studio</h1>
            <Badge tone="neutral">{allRows.length} templates</Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View Switcher: Showcase vs Live Studio */}
          <div className="flex items-center rounded-lg border border-line bg-paper p-0.5 shadow-2xs dark:bg-[#1f1c1a]">
            <button
              type="button"
              onClick={() => setViewMode("showcase")}
              aria-label="Showcase Gallery View"
              title="Showcase Gallery View"
              className={cn(
                "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition",
                viewMode === "showcase"
                  ? "bg-brand text-white shadow-2xs"
                  : "text-ink-3 hover:text-ink hover:bg-soft",
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Showcase</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("split")}
              aria-label="Split Live Studio View"
              title="Split Live Studio View"
              className={cn(
                "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition",
                viewMode === "split"
                  ? "bg-brand text-white shadow-2xs"
                  : "text-ink-3 hover:text-ink hover:bg-soft",
              )}
            >
              <ColumnsIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Live Studio</span>
            </button>

          </div>

          <button
            type="button"
            className={primary}
            onClick={() => setShowChoice(true)}
            aria-label="New Template"
          >
            <Plus className="h-4 w-4" />
            <span>New Template</span>
          </button>
        </div>
      </div>

      {/* ── Executive Metric Ribbon (Clickable Instant Filters) ── */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-5">
        <button
          type="button"
          onClick={() => {
            setStatusFilter("all");
            setKindFilter("all");
          }}
          className={cn(
            "flex flex-col items-start rounded-xl border p-2.5 text-left transition-all duration-200",
            statusFilter === "all" && kindFilter === "all"
              ? "border-brand bg-brand-soft/25 ring-1 ring-brand/40 shadow-xs"
              : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
          )}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
            Total Templates
          </span>
          <span className="mt-0.5 font-mono text-base font-bold text-ink">
            {allRows.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("active")}
          className={cn(
            "flex flex-col items-start rounded-xl border p-2.5 text-left transition-all duration-200",
            statusFilter === "active"
              ? "border-emerald-500 bg-emerald-50/70 ring-1 ring-emerald-500/40 shadow-xs dark:bg-emerald-950/30"
              : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
          )}
        >
          <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Approved
          </span>
          <span className="mt-0.5 font-mono text-base font-bold text-ink">
            {activeCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("pending")}
          className={cn(
            "flex flex-col items-start rounded-xl border p-2.5 text-left transition-all duration-200",
            statusFilter === "pending"
              ? "border-amber-500 bg-amber-50/70 ring-1 ring-amber-500/40 shadow-xs dark:bg-amber-950/30"
              : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
          )}
        >
          <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <Clock className="h-3 w-3" />
            In Review
          </span>
          <span className="mt-0.5 font-mono text-base font-bold text-ink">
            {pendingCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("draft")}
          className={cn(
            "flex flex-col items-start rounded-xl border p-2.5 text-left transition-all duration-200",
            statusFilter === "draft"
              ? "border-line bg-soft ring-1 ring-line shadow-xs"
              : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
          )}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
            Drafts
          </span>
          <span className="mt-0.5 font-mono text-base font-bold text-ink">
            {draftCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setKindFilter("carousel")}
          className={cn(
            "hidden lg:flex flex-col items-start rounded-xl border p-2.5 text-left transition-all duration-200",
            kindFilter === "carousel"
              ? "border-amber-500 bg-amber-50/70 ring-1 ring-amber-500/40 shadow-xs dark:bg-amber-950/30"
              : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
          )}
        >
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <Layers className="h-3 w-3" />
            Carousels
          </span>
          <span className="mt-0.5 font-mono text-base font-bold text-ink">
            {carouselCount}
          </span>
        </button>
      </div>

      {/* ── Filter & Search Command Ribbon ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line/60 bg-paper-card p-3 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
        {/* Search Input */}
        <div className="relative min-w-[200px] flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
          <input
            type="text"
            placeholder="Search templates by name, body, category... ( / )"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-full rounded-lg border border-line bg-paper pl-8 pr-7 text-xs text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a]"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Tabs */}
          <div className="flex items-center rounded-lg border border-line bg-soft/50 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className={cn(
                "rounded-md px-2.5 py-1 font-medium transition text-[11px]",
                statusFilter === "all"
                  ? "bg-paper-card text-ink shadow-2xs dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("active")}
              className={cn(
                "rounded-md px-2.5 py-1 font-medium transition text-[11px]",
                statusFilter === "active"
                  ? "bg-paper-card text-ink shadow-2xs dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              Approved
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("pending")}
              className={cn(
                "rounded-md px-2.5 py-1 font-medium transition text-[11px]",
                statusFilter === "pending"
                  ? "bg-paper-card text-ink shadow-2xs dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              Pending
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("draft")}
              className={cn(
                "rounded-md px-2.5 py-1 font-medium transition text-[11px]",
                statusFilter === "draft"
                  ? "bg-paper-card text-ink shadow-2xs dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              Drafts
            </button>
          </div>

          {/* Format Filter */}
          <div className="w-36 shrink-0">
            <Dropdown
              ariaLabel="Format filter"
              size="sm"
              value={kindFilter}
              onChange={(v) => setKindFilter(v as KindFilter)}
              options={[
                { value: "all", label: "All Formats" },
                { value: "normal", label: "Single Message" },
                { value: "carousel", label: "Carousel (Cards)" },
              ]}
            />
          </div>

          {/* Category Filter */}
          <div className="w-36 shrink-0">
            <Dropdown
              ariaLabel="Category filter"
              size="sm"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: "all", label: "All Categories" },
                { value: "MARKETING", label: "Marketing" },
                { value: "UTILITY", label: "Utility" },
                { value: "AUTHENTICATION", label: "Authentication" },
              ]}
            />
          </div>

          {/* Sort By Dropdown */}
          <div className="w-40 shrink-0">
            <Dropdown
              ariaLabel="Sort templates"
              size="sm"
              value={sortBy}
              onChange={(v) => setSortBy(v as any)}
              options={[
                { value: "name", label: "Sort: Name (A-Z)" },
                { value: "status", label: "Sort: Review Status" },
                { value: "cards", label: "Sort: Most Cards" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Loading & Error States ── */}
      {list.isLoading && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-brand" />
          <p className="mt-3 text-sm text-ink-3">Loading WhatsApp templates…</p>
        </div>
      )}

      {list.isError && (
        <div
          role="alert"
          className="rounded-xl border border-danger/30 bg-danger-soft p-5 text-sm text-danger shadow-sm"
        >
          <p className="font-semibold">Could not load templates.</p>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 font-semibold underline"
            onClick={() => list.refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      )}

      {/* Empty Search Result */}
      {!list.isLoading && !list.isError && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-line/60 bg-paper-card py-16 px-4 text-center shadow-card dark:bg-[#292524]">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-soft text-ink-3">
            <Search className="h-6 w-6" />
          </div>
          <p className="mt-3 text-sm font-semibold text-ink">No templates found</p>
          <p className="mt-1 text-xs text-ink-3 max-w-sm">
            Try adjusting your search terms or filters to find what you are looking for.
          </p>
          <button
            type="button"
            className={`${secondary} mt-4`}
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setKindFilter("all");
              setCategoryFilter("all");
            }}
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* ── VIEW 1: SHOWCASE GALLERY (Refined 3-Column Responsive Cards) ── */}
      {!list.isLoading && !list.isError && viewMode === "showcase" && filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((t) => {
              const isCarousel = t.template_kind === "carousel";
              const rawStatus = (t.status || "draft").toLowerCase();
              const isApproved = rawStatus === "approved" || rawStatus === "active";
              const isPending = rawStatus === "pending";
              const isRejected = rawStatus === "rejected";

              let KindIcon = FileText;
              let iconStyle = "bg-brand-soft text-brand border-brand/20";
              if (isCarousel) {
                KindIcon = Layers;
                iconStyle =
                  "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400";
              } else if (t.type === "VIDEO") {
                KindIcon = Video;
                iconStyle =
                  "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400";
              } else if (t.type === "IMAGE") {
                KindIcon = ImageIcon;
                iconStyle =
                  "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400";
              }

              return (
                <motion.article
                  layout
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  key={t.template_id}
                  onClick={() =>
                    navigate(`/whatsapp/templates/${encodeURIComponent(t.template_id)}`)
                  }
                  className="group relative flex flex-col justify-between rounded-2xl border border-line/70 bg-paper-card p-4 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-card-hover dark:bg-[#292524] dark:shadow-card-dark cursor-pointer"
                >
                  <div>
                    {/* Top Header: Kind Icon + Title + Status Badge */}
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border shadow-2xs transition group-hover:scale-105",
                            iconStyle,
                          )}
                        >
                          <KindIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <h2
                            className="truncate text-sm font-bold text-ink transition group-hover:text-brand"
                            title={t.name}
                          >
                            {t.name}
                          </h2>
                          <div className="mt-0.5 flex items-center gap-1.5">
                            <span className="font-mono text-[10px] text-ink-3">
                              {isCarousel ? "Carousel" : t.type || "Text"}
                            </span>
                            <span className="text-line">•</span>
                            <span className="font-mono text-[10px] font-semibold uppercase text-ink-3">
                              {t.category || "MARKETING"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Status Tag + Action Menu */}
                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isApproved && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-sky-700 dark:bg-sky-950/40 dark:text-sky-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                            Approved
                          </span>
                        )}
                        {isPending && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                            <Clock className="h-2.5 w-2.5" />
                            Pending
                          </span>
                        )}
                        {isRejected && (
                          <span
                            title={t.meta_rejection_reason || "Rejected by Meta"}
                            className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-danger-soft px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-danger"
                          >
                            <XCircle className="h-2.5 w-2.5" />
                            Rejected
                          </span>
                        )}
                        {!isApproved && !isPending && !isRejected && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-soft px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-ink-3">
                            Draft
                          </span>
                        )}

                        <ActionMenu
                          ariaLabel={`Actions for ${t.name}`}
                          items={[
                            {
                              label: "Preview & Test",
                              icon: <Eye className="h-4 w-4 text-brand mr-1" />,
                              onClick: () => setPreviewTarget(t),
                            },
                            {
                              label: "Open in Studio",
                              icon: <ExternalLink className="h-4 w-4 mr-1" />,
                              onClick: () =>
                                navigate(`/whatsapp/templates/${encodeURIComponent(t.template_id)}`),
                            },
                            {
                              label: "Copy Template Text",
                              icon: <Copy className="h-4 w-4 mr-1" />,
                              onClick: () => {
                                navigator.clipboard.writeText(t.body);
                                toast.success("Template body copied to clipboard");
                              },
                            },
                            {
                              label: "Delete Template",
                              danger: true,
                              icon: <Trash2 className="h-4 w-4 text-danger mr-1" />,
                              onClick: () => setPendingDelete(t),
                            },
                          ]}
                        />
                      </div>
                    </div>

                    {/* WhatsApp Authentic Bubble Canvas */}
                    <div className="mt-3 overflow-hidden rounded-xl border border-line/60 bg-[#efeae2]/75 p-2.5 dark:bg-[#0c1317]">
                      <WhatsAppMessageBubble
                        template={t}
                        compact
                        onButtonClick={(b) => {
                          setPreviewTarget(t);
                        }}
                      />
                    </div>
                  </div>

                  {/* Card Bottom Strip */}
                  <div className="mt-3.5 flex items-center justify-between gap-2 border-t border-line/50 pt-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="rounded bg-soft px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase text-ink-3">
                        {t.language || "en"}
                      </span>
                      {(t.variables?.length ?? 0) > 0 && (
                        <span className="rounded bg-sky-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                          {t.variables?.length} var{t.variables?.length === 1 ? "" : "s"}
                        </span>
                      )}
                      {isCarousel && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                          {t.cards?.length ?? 0} cards
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border border-line bg-paper px-2.5 py-1 text-xs font-semibold text-ink transition hover:bg-soft hover:text-brand dark:bg-[#1f1c1a]"
                        onClick={() => setPreviewTarget(t)}
                        title="Interactive WhatsApp Tester"
                      >
                        <Eye className="h-3.5 w-3.5 text-brand" />
                        <span>Test</span>
                      </button>

                      <button
                        type="button"
                        className={`${primary} text-xs py-1 px-2.5`}
                        onClick={() =>
                          navigate(`/whatsapp/templates/${encodeURIComponent(t.template_id)}`)
                        }
                      >
                        <span>Studio</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* ── VIEW 2: SPLIT LIVE STUDIO (The "Out of the Box" WOW Factor) ── */}
      {!list.isLoading && !list.isError && viewMode === "split" && filtered.length > 0 && (
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          {/* Left Column: High-density template list */}
          <div className="space-y-2.5 lg:col-span-7">
            <div className="flex items-center justify-between px-1 text-xs font-semibold text-ink-3">
              <span>Templates ({filtered.length})</span>
              <span>Click a template to test live</span>
            </div>

            <div className="space-y-2 max-h-[750px] overflow-y-auto pr-1">
              {filtered.map((t) => {
                const isSelected = activeSplitTemplate?.template_id === t.template_id;
                const isCarousel = t.template_kind === "carousel";
                const rawStatus = (t.status || "draft").toLowerCase();
                const isApproved = rawStatus === "approved" || rawStatus === "active";
                const isPending = rawStatus === "pending";

                return (
                  <div
                    key={t.template_id}
                    onClick={() => {
                      setSelectedTemplateId(t.template_id);
                      // initialize dynamic test values
                      const init: Record<string, string> = {};
                      (t.variables ?? []).forEach((v, idx) => {
                        init[String(v)] = idx === 0 ? "John Doe" : idx === 1 ? "25% OFF" : `Val_${v}`;
                      });
                      setSplitVars(init);
                    }}
                    className={cn(
                      "flex items-center justify-between gap-3 rounded-xl border p-3 transition-all cursor-pointer",
                      isSelected
                        ? "border-brand bg-brand-soft/20 shadow-xs ring-1 ring-brand/40"
                        : "border-line/70 bg-paper-card hover:border-line hover:bg-soft/40 dark:bg-[#292524]",
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border",
                          isCarousel
                            ? "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/30"
                            : "bg-brand-soft text-brand border-brand/20",
                        )}
                      >
                        {isCarousel ? <Layers className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-xs font-bold text-ink">{t.name}</p>
                          {isApproved && (
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                          )}
                          {isPending && (
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                          )}
                        </div>
                        <p className="line-clamp-1 text-[11px] text-ink-3">{t.body}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="rounded bg-soft px-1.5 py-0.5 font-mono text-[9px] font-semibold text-ink-3 uppercase">
                        {t.category || "MARKETING"}
                      </span>
                      <ArrowRight className={cn("h-4 w-4 text-ink-3 transition-transform", isSelected && "text-brand translate-x-0.5")} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Sticky Live WhatsApp Phone Simulator */}
          <div className="lg:col-span-5 sticky top-4 flex justify-center">
            {activeSplitTemplate && (
              <LiveSmartphoneDevice
                template={activeSplitTemplate}
                variableValues={splitVars}
                onVariableChange={(vNum, val) =>
                  setSplitVars((prev) => ({ ...prev, [vNum]: val }))
                }
                onOpenStudio={() =>
                  navigate(`/whatsapp/templates/${encodeURIComponent(activeSplitTemplate.template_id)}`)
                }
              />
            )}
          </div>
        </div>
      )}


      {/* ── Format Selection Modal (New Template) ── */}
      {showChoice && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setShowChoice(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-line bg-elevated p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-ink">Choose Template Architecture</h3>
                <p className="mt-1 text-xs text-ink-3">
                  Select the structural format for your Meta WhatsApp template
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowChoice(false)}
                className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink"
              >
                ✕
              </button>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                className="group flex flex-col justify-between rounded-xl border border-line/80 bg-paper p-4 text-left transition hover:border-brand hover:shadow-card dark:bg-[#1f1c1a]"
                onClick={() => {
                  setShowChoice(false);
                  navigate("/whatsapp/templates/new");
                }}
              >
                <div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand transition group-hover:scale-105">
                    <FileText className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-bold text-ink group-hover:text-brand">
                    Standard Template
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-3">
                    Header media (Image/Video/Text), personalized body with dynamic variables, and call-to-action buttons.
                  </p>
                </div>
                <span className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-brand">
                  Baileys + Meta Cloud-API →
                </span>
              </button>

              <button
                type="button"
                className="group flex flex-col justify-between rounded-xl border border-line/80 bg-paper p-4 text-left transition hover:border-amber-500 hover:shadow-card dark:bg-[#1f1c1a]"
                onClick={() => {
                  setShowChoice(false);
                  navigate("/whatsapp/templates/new?kind=carousel");
                }}
              >
                <div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition group-hover:scale-105 dark:bg-amber-950/40 dark:text-amber-400">
                    <Layers className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-bold text-ink group-hover:text-amber-600">
                    Carousel Template
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-3">
                    Interactive multi-card gallery with 2–10 horizontal swipe cards, individual media, and buttons.
                  </p>
                </div>
                <span className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                  Meta Cloud-API Verified →
                </span>
              </button>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className={secondary}
                onClick={() => setShowChoice(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Live Variable & Interaction Simulator Modal ── */}
      {previewTarget && (
        <TemplateLiveTesterModal
          template={previewTarget}
          onClose={() => setPreviewTarget(null)}
          onOpenStudio={() => {
            navigate(`/whatsapp/templates/${encodeURIComponent(previewTarget.template_id)}`);
            setPreviewTarget(null);
          }}
        />
      )}

      {/* ── Confirm Delete Modal ── */}
      {pendingDelete && (
        <ConfirmModal
          title="Delete template"
          body={`Are you sure you want to delete template "${pendingDelete.name}"? This action cannot be undone.`}
          confirmLabel={del.isPending ? "Deleting…" : "Delete template"}
          onConfirm={() => del.mutate(pendingDelete.template_id)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
